import React from 'react';
import CircuitBreakerStatus, { CircuitBreakerDashboard } from './CircuitBreakerStatus';
import Button from './Button';
import { getAllCircuitBreakerStatus, CIRCUIT_STATES, getCircuitBreaker, HEALTHCHECK_MODE } from '../utils/circuit-breaker';
import { ERROR_TYPES } from '../constants/circuit-breaker-constants';
import { ROUTES_CONFIG } from '../config/routes-config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClock } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '../context/TranslationContext';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';
import { requestWatchdogResetViaKV, mapRouteToWorkerName } from '../utils/circuit-breaker-kv-reset.js';
// Les styles CSS sont importés globalement via styles/index.js

/**
 * Page d'administration pour gérer et tester les circuit breakers
 */
const CircuitBreakerAdmin = () => {
  const { t, loadPageTranslations } = useTranslation();
  const [allStatus, setAllStatus] = React.useState({});
  const [lastUpdate, setLastUpdate] = React.useState(new Date());
  const [testingRoutes, setTestingRoutes] = React.useState(new Set());

  // Charger les traductions au montage du composant
  React.useEffect(() => {
    loadPageTranslations('ADMIN_CIRCUIT_BREAKER');
  }, [loadPageTranslations]);

  // Mettre à jour le statut toutes les 5 secondes (moins fréquent pour éviter le clignotement)
  React.useEffect(() => {
    const updateStatus = () => {
      const newStatus = getAllCircuitBreakerStatus();
      
      // Seulement mettre à jour si les statuts ont réellement changé
      setAllStatus(prevStatus => {
        const hasChanged = JSON.stringify(prevStatus) !== JSON.stringify(newStatus);
        if (hasChanged) {
          setLastUpdate(new Date());
        }
        return hasChanged ? newStatus : prevStatus;
      });
    };

    // Mise à jour initiale
    updateStatus();

    const interval = setInterval(updateStatus, 5000); // Changé de 2s à 5s

    return () => clearInterval(interval);
  }, []);

  /**
   * Fonction pour forcer le test d'un circuit breaker
   * Mémorisée pour éviter les re-créations inutiles
   */
  const forceTestCircuitBreaker = React.useCallback(async (route) => {
    setTestingRoutes(prev => new Set([...prev, route]));
    
    try {
      // 1. Réinitialiser le watchdog en local
      const breaker = getCircuitBreaker(route);
      if (breaker) {
        breaker.resetWatchdog();
        debug.log(config, `Watchdog réinitialisé localement pour ${route}`);
      }
      
      // 2. Demander un reset du watchdog via KV pour que le worker le détecte
      const workerName = mapRouteToWorkerName(route);
      if (workerName && workerName !== 'unknown') {
        try {
          await requestWatchdogResetViaKV(workerName);
          debug.log(config, `Demande de reset KV envoyée pour ${workerName}`);
        } catch (kvError) {
          // Ne pas échouer la fonction si le reset KV échoue
          // Le reset local est déjà fait
          debug.warn(config, `Erreur lors du reset KV pour ${workerName}:`, kvError);
        }
      }
      
      // 3. Forcer un health check
      if (breaker) {
        await breaker.checkHealth(HEALTHCHECK_MODE.USER);
      }
    } catch (error) {
      debug.error(config, `Erreur lors du test du circuit breaker pour ${route}:`, error);
    } finally {
      // Retirer la route de la liste des tests en cours après un délai
      setTimeout(() => {
        setTestingRoutes(prev => {
          const newSet = new Set(prev);
          newSet.delete(route);
          return newSet;
        });
      }, 1000);
    }
  }, []);

  /**
   * Composant pour afficher une ligne de circuit breaker avec bouton de test
   * Mémorisé pour éviter les re-renders inutiles
   */
  const CircuitBreakerRow = React.useCallback(({ label, route }) => {
    const isTestingRoute = testingRoutes.has(route);
    
    return (
      <div className="wa-form-group wa-form-group-compact">
        <label className="wa-label">
          {label}
        </label>
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            <CircuitBreakerStatus 
              route={route}
              type="large"
              style={{ display: 'block', width: '100%' }}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            loading={isTestingRoute}
            className="ml-2 flex-shrink-0"
            onClick={() => forceTestCircuitBreaker(route)}
            title={t('ADMIN_CIRCUIT_BREAKER', 'FORCE_TEST_TITLE', 'Force test circuit-breaker')}
          >
            <span>✓</span>
          </Button>
        </div>
      </div>
    );
  }, [testingRoutes, forceTestCircuitBreaker, t]);

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">
        {t('ADMIN_CIRCUIT_BREAKER', 'PAGE_TITLE', 'Circuit Breaker - Administration')}
      </h1>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">
          {t('ADMIN_CIRCUIT_BREAKER', 'GLOBAL_DASHBOARD', 'Dashboard Global')}
        </h2>
        <CircuitBreakerDashboard />
        <p className="text-sm text-gray-500 mt-2">
          {t('ADMIN_CIRCUIT_BREAKER', 'LAST_UPDATE', 'Dernière mise à jour : {{time}}', { time: lastUpdate.toLocaleTimeString() })}
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">
          {t('ADMIN_CIRCUIT_BREAKER', 'SERVICES_MANAGEMENT', 'Gestion des Services')}
        </h2>
        <div className="wa-form-group">
          {ROUTES_CONFIG.map(routeConfig => (
            <CircuitBreakerRow 
              key={routeConfig.route}
              label={routeConfig.displayName} 
              route={routeConfig.route} 
            />
          ))}
        </div>
        <div className="mt-4 p-3 rounded-lg" style={{backgroundColor: 'var(--color-primary-light)'}}>
          <p className="text-sm" style={{color: 'var(--color-primary)'}}>
            <strong>{t('ADMIN_CIRCUIT_BREAKER', 'NOTE_LABEL', 'Note :')}</strong> {t('ADMIN_CIRCUIT_BREAKER', 'TEST_NOTE', 'Le bouton de test force une vérification de santé et remet à zéro le watchdog pour le service sélectionné.')}
          </p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">
          {t('ADMIN_CIRCUIT_BREAKER', 'DETAILED_STATUS', 'Statut Détaillé (JSON)')}
        </h2>
        <pre className="circuit-breaker-debug text-xs overflow-auto max-h-64">
{JSON.stringify(allStatus, null, 2)}
        </pre>
      </div>

      <div className="p-4 rounded-lg" style={{backgroundColor: 'var(--color-primary-light)'}}>
        <h3 className="font-medium mb-2" style={{color: 'var(--color-primary)'}}>
          {t('ADMIN_CIRCUIT_BREAKER', 'STATE_LEGEND', 'Légende des États')}
        </h3>
        <div className="circuit-breaker-legend">
          <div className="legend-item">
            <div className="legend-example">
              <span className="circuit-breaker-status status-operational">
                <span className="service-icon">
                  &nbsp;
                </span>
                <span className="icon-spacer"></span>
                <span className="status-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
              </span>
            </div>
            <div className="legend-description">
              {t('ADMIN_CIRCUIT_BREAKER', 'STATE_OPERATIONAL', 'Service opérationnel (CLOSED)')}
            </div>
          </div>
          
          <div className="legend-item">
            <div className="legend-example">
              <span className="circuit-breaker-status status-recovering">
                <span className="service-icon">
                &nbsp;
                </span>
                <span className="icon-spacer"></span>
                <span className="status-icon">
                  <FontAwesomeIcon icon={faClock} />
                </span>
              </span>
            </div>
            <div className="legend-description">
              {t('ADMIN_CIRCUIT_BREAKER', 'STATE_RECOVERING', 'Test de récupération (HALF_OPEN)')}
            </div>
          </div>
          
          {Object.values(ERROR_TYPES).map(errorType => (
            <div key={errorType.type} className="legend-item">
              <div className="legend-example">
                <span className={`circuit-breaker-status status-error ${errorType.cssClass}`}>
                  <span className="service-icon">
                  &nbsp;
                  </span>
                  <span className="icon-spacer"></span>
                  <span className="status-icon">
                    <FontAwesomeIcon icon={errorType.icon} />
                  </span>
                </span>
              </div>
              <div className="legend-description">
                {t('ADMIN_CIRCUIT_BREAKER', 'ERROR_TYPE', 'Erreur {{errorType}} ({{type}})', { 
                  errorType: errorType.defaultText, 
                  type: errorType.type 
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">
          {t('ADMIN_CIRCUIT_BREAKER', 'SERVICE_ICONS', 'Icônes des Services')}
        </h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {ROUTES_CONFIG.map(routeConfig => (
            <div key={routeConfig.route} className="flex items-center space-x-2">
              <span className="w-6 h-6 flex items-center justify-center">
                <FontAwesomeIcon icon={routeConfig.serviceIcon} className="text-gray-600" />
              </span>
              <span>{routeConfig.displayName}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CircuitBreakerAdmin;
